import * as React from "react";
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useParams,
  useRouteMatch,
} from "react-router";
import { ClinicDashBoardRoute, ClinicRoute } from "../../clinic/ClinicRoutes";
import { ClinicProvider } from "../../clinic/clinicStateContext";
import { ClinicManage } from "./clinicManage";
import { OnboardRouter } from "./onBoarding/onboardRouter";
import { ClinicLoading } from "./uiTemplates/clinicLoading";
import { ClinicInvitePrint } from "./patients/invite/clinicInvitePrint";
import { ClinicPatientCard } from "./clinicPatientCard";
import { ClinicConfigKeys } from "fe-common/graphql/clinic/clinicConfigKeys";
import { useDaoContext } from "fe-web-components/diseaseview/DaoContext";
import { MergedConfigurationSettings } from "@kahunmedical/common-configuration";
import { IntlProvider } from "react-intl";
import { ClinicGeneralOptions } from "@kahunmedical/common-configuration/dist/configuration/clinic";
import { useEffect, useMemo, useState } from "react";
import { ClinicVimPatientCard } from "./clinicVimPatientCard";
import { getV2ClinicSettings } from "fe-web-components/settings/common/getV2ClinicSettings";
import { useClinicContext } from "fe-web-components/clinic/ClinicContext";
import { useLocaleContext } from "fe-common/locale/localeContext";
import { useClinicUser } from "./hooks/useClinicUser";
import { ClinicUserProvider } from "./clinicUserContext";
import { ErrorMessage } from "./errorMessage";
import {
  DEMO_CLINIC_ID,
  MASTER_CLINIC_ID,
} from "fe-common/commonTypes/masterClinicDefaults";
import { removeTrailingSlash } from "fe-common/routes/removeTrailingSlash";
import { CLINIC_MANAGE_URL } from "./clinicSettings/clinicUrls";
import { ClinicData } from "fe-common/graphql";
import { ClinicMessagesProvider } from "./clinicMessagesContext";
//region [[ Styles ]]

//endregion [[ Styles ]]

//region [[ Props ]]

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const ClinicStateRouter = () => {
  const { id: clinicId }: { id: string } = useParams();

  const match = useRouteMatch();
  const currURL = removeTrailingSlash(match.url);
  const history = useHistory();

  const { isClinicUser, ...clinicUser } = useClinicUser(clinicId);

  const {
    locale,
    changeLocale,
    isLoadingTranslations,
    translations,
  } = useLocaleContext();

  const webDao = useDaoContext();
  const clinicDao = useClinicContext();

  if (clinicId && Object.keys(ClinicRoute).includes(clinicId) && !isClinicUser)
    sessionStorage.setItem("requestedClinicId", clinicId);
  //save for after login
  else {
    sessionStorage.removeItem("requestedClinicId");
  }

  const [clinicData, setClinicData] = useState<ClinicData>(null);
  const [isMasterClinic, setIsMasterClinic] = useState(false);
  const [loadingClinicData, setLoadingClinicData] = useState(false);
  const [defaultLocale, setDefaultLocale] = useState("en");
  const [baseLocale, setBaseLocale] = useState("en");

  useEffect(() => {
    if (clinicId && isClinicUser) {
      setLoadingClinicData(true);
      clinicDao.clinicData(clinicId).then((res) => {
        setIsMasterClinic(
          clinicId === MASTER_CLINIC_ID || clinicId === DEMO_CLINIC_ID,
        );
        setClinicData(res);
        setLoadingClinicData(false);
      });
    }
  }, [clinicDao, clinicId, isClinicUser]);

  const gotoCase = (codeId: string) => {
    history.push(
      `${CLINIC_MANAGE_URL}/${clinicId}/patients${codeId ? "/" + codeId : ""}`,
    );
  };

  const clinicMergedConfig: any = useMemo(() => {
    if (clinicData?.config) {
      const mergedSettings = new MergedConfigurationSettings(
        {},
        clinicData?.mergedConfiguration || [
          clinicData?.name,
          getV2ClinicSettings(clinicData?.config),
        ],
      );

      webDao.updateSettingsObject(mergedSettings);
      // tbd, what about getFeatureFlag? shouldn't this also use the clinic config
      webDao.updateExternalFeatureFlags(
        clinicData?.config?.[ClinicConfigKeys.SolverFeatureFlags],
      );

      const clinicConfig = mergedSettings?.makeConfigObjectForKey(
        ClinicGeneralOptions,
        "clinic",
      );

      //adding text overrides to config
      clinicConfig[ClinicConfigKeys.Translations] =
        clinicData?.config?.[ClinicConfigKeys.Translations];

      //adding LLM support indication to config
      clinicConfig[ClinicConfigKeys.UsesLLM] = mergedSettings?.getValueForKey(
        "conversation.resultsDD.useLLM",
      )?.value; //adding Assessment Grounding indication to config
      clinicConfig[ClinicConfigKeys.UsesRag] = mergedSettings?.getValueForKey(
        "conversation.resultsDD.llmOptions.useRag",
      )?.value;
      clinicConfig[
        ClinicConfigKeys.GroundAssessment
      ] = mergedSettings?.getValueForKey(
        "conversation.resultsDD.llmOptions.groundAssessment",
      )?.value; //adding DD Grounding indication to config
      clinicConfig[
        ClinicConfigKeys.GroundExplanation
      ] = mergedSettings?.getValueForKey(
        "conversation.resultsDD.llmOptions.groundExplanation",
      )?.value;

      return clinicConfig;
    }
    return null;
  }, [clinicData, webDao]);

  useEffect(() => {
    if (clinicMergedConfig) {
      const managementLocale =
        clinicMergedConfig.clinicManageLocale ||
        clinicMergedConfig.defaultLocale;
      setDefaultLocale(managementLocale);
      const language = managementLocale.split("_")[0];
      setBaseLocale(language); //remove country suffix from locale <- should be canceled if using custom translations for a country
      changeLocale(language);
    }
  }, [clinicMergedConfig, changeLocale]);

  useEffect(() => {
    webDao.addHeaders({ "kahun-langcode": locale });
  }, [webDao, locale]);

  useEffect(() => {
    clinicDao.addHeaders({ "kahun-langcode": locale });
  }, [clinicDao, locale]);

  if (!clinicUser.loading && !clinicUser.isLoggedIn) {
    return <Redirect to={`${CLINIC_MANAGE_URL}/${ClinicRoute.Login}`} />;
  }

  if (clinicUser.loading || loadingClinicData || isLoadingTranslations)
    return (
      <ClinicLoading
        title={
          clinicUser.loading ? "Connecting to clinic" : "Loading clinic data"
        }
      />
    );

  if (clinicUser.errMsg) return <ErrorMessage />;

  return (
    <ClinicUserProvider value={{ isClinicUser, ...clinicUser }}>
      <ClinicMessagesProvider>
        <ClinicProvider
          value={{
            isMasterClinic,
            isDemoClinic:
              clinicData?.config[ClinicConfigKeys.UnifiedConfigString]?.[
                "clinic.demoMode"
              ],
            defaultLocale,
            clinicConfig: clinicMergedConfig,
            clinicData,
            privateMode: false,
            gotoCase,
          }}
        >
          <IntlProvider
            messages={translations}
            locale={locale}
            defaultLocale={baseLocale}
          >
            <Switch>
              {!clinicId && !clinicUser.userClinics?.length && (
                // user has no clinics - redirect to create a new one */
                <Redirect to={`${currURL}/${ClinicRoute.New}`} />
              )}
              {!clinicId && clinicUser.userClinics?.length && (
                //no clinic was chosen - go to the first clinic
                <Redirect
                  to={`${currURL}/${
                    clinicUser.userClinics.sort((a, b) => {
                      return a.isFavorite ? -1 : 1;
                    })[0].id
                  }`}
                />
              )}
              <Route
                path={`${currURL}/${ClinicDashBoardRoute.InvitePrint}`}
                component={ClinicInvitePrint}
                exact
              />
              <Route
                path={`${currURL}/${ClinicDashBoardRoute.PatientCard}`}
                component={ClinicPatientCard}
                exact
              />
              <Route
                path={`${currURL}/${ClinicDashBoardRoute.EhrIntegPatientCard}`}
                component={ClinicVimPatientCard}
                exact
              />
              {/* Safety check, if the user isn't valid, don't allow the dashboard to
            even load */}
              <Route
                render={() =>
                  isClinicUser ? (
                    <ClinicManage />
                  ) : (
                    <OnboardRouter invalidClinic={!isClinicUser} />
                  )
                }
              />
            </Switch>
          </IntlProvider>
        </ClinicProvider>
      </ClinicMessagesProvider>
    </ClinicUserProvider>
  );
};
